import moment from 'moment';

/**
 * Returns a human-friendly relative time string for a timestamp.
 * Supports both past ("2 days ago") and future ("in 2 days") values.
 * Granularity covered: minutes, days, weeks, months, years. Values under 1 minute => "Just now".
 * Examples:
 *  - timeAgo('2025-08-10T10:00:00Z') => '2 days ago'
 *  - timeAgo('2025-08-14T10:00:00Z') => 'in 2 days'
 */
export const timeAgo = (timestamp: string | Date | undefined | null): string => {
  if (!timestamp) return '';

  const target = moment(timestamp);
  if (!target.isValid()) return '';

  const now = moment();
  const diffMs = target.diff(now); // positive => future
  const diffAbsMs = Math.abs(diffMs);

  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const weekMs = 7 * dayMs;
  const monthMs = 30 * dayMs; // approximation for display
  const yearMs = 365 * dayMs; // approximation for display

  if (diffAbsMs < minuteMs) {
    return 'Just now';
  }

  const format = (value: number, unit: string) => {
    const plural = value === 1 ? unit : unit + 's';
    return diffMs > 0 ? `in ${value} ${plural}` : `${value} ${plural} ago`;
  };

  // Minutes (< 1 hour -> minutes)
  if (diffAbsMs < hourMs) {
    const minutes = Math.floor(diffAbsMs / minuteMs);
    return format(minutes, 'minute');
  }

  // For anything >= 1 hour we jump straight to days granularity per requirements.
  const days = Math.floor(diffAbsMs / dayMs);
  if (days < 7) {
    if (days === 0) {
      // Less than 1 day but >= 1 hour -> express in hours to avoid "0 days"
      const hours = Math.floor(diffAbsMs / hourMs);
      return format(hours, 'hour');
    }
    return format(days, 'day');
  }

  // Weeks (< ~4 weeks)
  const weeks = Math.floor(diffAbsMs / weekMs);
  if (weeks < 4) {
    return format(weeks, 'week');
  }

  // Months (< 12 months)
  const months = Math.floor(diffAbsMs / monthMs);
  if (months < 12) {
    return format(months, 'month');
  }

  // Years
  const years = Math.floor(diffAbsMs / yearMs);
  return format(years, 'year');
};
